import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import type { NostrEvent } from '@nostrify/nostrify';

export const SIMPLY_NOSTR_PUBKEY = 'a4a6e5f447e1561723d557030888a158b9be09f856a006ddd44021e38ebd01fd';

export const EPISODE_RELAYS = [
  'wss://relay.damus.io',
  'wss://relay.primal.net',
  'wss://nos.lol',
  'wss://relay.nostr.band',
  'wss://relay.ditto.pub',
];

export interface Episode {
  event: NostrEvent;
  videoUrl: string | null;
  thumbnailUrl: string | null;
  title: string;
  description: string;
  guestPubkey: string | null;
  episodeNumber: number | null;
  hashtags: string[];
}

function hasVideo(content: string): boolean {
  return (
    content.includes('cdn.satellite.earth') ||
    content.includes('satellite.earth') ||
    content.includes('v.nostr.build') ||
    /https?:\/\/[^\s]+\.(mp4|webm|mov|MP4|MOV)/.test(content)
  );
}

function extractVideoUrl(content: string): string | null {
  const urls = content.match(/https?:\/\/[^\s\n]+/g) ?? [];
  for (const raw of urls) {
    const url = raw.replace(/[).,!?\s]+$/, '');
    if (
      url.includes('cdn.satellite.earth') ||
      url.includes('satellite.earth') ||
      url.includes('v.nostr.build') ||
      /\.(mp4|webm|mov|MP4|MOV)(\?|$)/.test(url)
    ) return url;
  }
  return null;
}

function extractImageUrl(content: string): string | null {
  const urls = content.match(/https?:\/\/[^\s\n]+/g) ?? [];
  for (const raw of urls) {
    const url = raw.replace(/[).,!?\s]+$/, '');
    if (/\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(url)) return url;
  }
  return null;
}

function extractGuestPubkey(event: NostrEvent): string | null {
  return event.tags.find(([t]) => t === 'p')?.[1] ?? null;
}

function extractHashtags(event: NostrEvent): string[] {
  const fromTags = event.tags.filter(([t]) => t === 't').map(([, v]) => v).filter(Boolean);
  if (fromTags.length > 0) return fromTags;
  return (event.content.match(/#([A-Za-z][A-Za-z0-9_]+)/g) ?? []).map(m => m.slice(1));
}

function extractEpisodeNumber(content: string): number | null {
  for (const pattern of [/[Ee]p\.?\s*(\d+)/, /[Ee]pisode\s*(\d+)/, /#(\d{1,3})(?:\s|$)/]) {
    const m = content.match(pattern);
    if (m) {
      const n = parseInt(m[1], 10);
      if (n >= 1 && n <= 999) return n;
    }
  }
  return null;
}

function extractTitle(content: string, episodeNumber: number | null): string {
  const firstLine = content
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.length > 0 && !l.startsWith('http'))[0] ?? '';
  if (firstLine.length > 0 && firstLine.length <= 120) return firstLine;
  if (episodeNumber) return `Simply Nostr — Episode ${episodeNumber}`;
  return 'Simply Nostr Episode';
}

function extractDescription(content: string): string {
  const lines = content.split('\n').map(l => l.trim()).filter(l => l.length > 0 && !l.startsWith('http'));
  if (lines.length <= 1) return '';
  const rest = lines.slice(1).join(' ').trim();
  return rest.length <= 300 ? rest : rest.substring(0, 300) + '…';
}

function parseEpisode(event: NostrEvent): Episode {
  const episodeNumber = extractEpisodeNumber(event.content);
  return {
    event,
    videoUrl: extractVideoUrl(event.content),
    thumbnailUrl: extractImageUrl(event.content),
    title: extractTitle(event.content, episodeNumber),
    description: extractDescription(event.content),
    guestPubkey: extractGuestPubkey(event),
    episodeNumber,
    hashtags: extractHashtags(event),
  };
}

export function useEpisodes() {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['episodes', SIMPLY_NOSTR_PUBKEY],
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [1], authors: [SIMPLY_NOSTR_PUBKEY], limit: 500 }],
        { signal: c.signal }
      );

      return events
        .filter(e => hasVideo(e.content))
        .map(parseEpisode)
        .sort((a, b) => b.event.created_at - a.event.created_at);
    },
    staleTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: 2000,
  });
}
